import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

/**
 * Règles alignées sur PasswordValidator côté back, seule autorité en la matière.
 *
 * Elles étaient auparavant réécrites à trois endroits, avec trois résultats différents : la page
 * héritée du profil acceptait 4 caractères, le volet 12 sans contrainte de composition, et la
 * modale omettait le chiffre tout en restreignant les caractères spéciaux plus que le back. Dans
 * les trois cas la saisie était acceptée puis refusée après envoi.
 */
export const PASSWORD_RULES: {
  isValid: (password: string) => boolean;
  errorMessage: string;
  /** Forme courte, pour l'affichage en liste de critères. */
  label: string;
}[] = [
  {
    isValid: password => password.length >= 12 && password.length <= 100,
    errorMessage: 'Le mot de passe doit contenir entre 12 et 100 caractères',
    label: 'Entre 12 et 100 caractères',
  },
  {
    isValid: password => /[A-Z]/.test(password),
    errorMessage: 'Le mot de passe doit contenir au moins une majuscule',
    label: 'Au moins une majuscule',
  },
  {
    isValid: password => /[a-z]/.test(password),
    errorMessage: 'Le mot de passe doit contenir au moins une minuscule',
    label: 'Au moins une minuscule',
  },
  {
    isValid: password => /[0-9]/.test(password),
    errorMessage: 'Le mot de passe doit contenir au moins un chiffre',
    label: 'Au moins un chiffre',
  },
  {
    // Même définition que le back : tout ce qui n'est ni lettre ni chiffre. Une lettre accentuée
    // est donc un caractère spécial, contrairement à ce que la liste de critères affichait.
    isValid: password => /[^a-zA-Z0-9]/.test(password),
    errorMessage: 'Le mot de passe doit contenir au moins un caractère spécial',
    label: 'Au moins un caractère spécial',
  },
];

/** Messages des règles non respectées, vide si le mot de passe est conforme. */
export function passwordRuleErrors(password: string): string[] {
  if (!password) {
    return [];
  }
  return PASSWORD_RULES.filter(rule => !rule.isValid(password)).map(
    rule => rule.errorMessage,
  );
}

/** Libellés courts des règles non respectées, pour la liste de critères. */
export function unmetPasswordRuleLabels(password: string): string[] {
  return PASSWORD_RULES.filter(rule => !rule.isValid(password || '')).map(
    rule => rule.label,
  );
}

/** Validateur de champ, pour les formulaires qui valident le mot de passe isolément. */
export function passwordRulesValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const errors = passwordRuleErrors(control.value);
    return errors.length > 0
      ? { invalidPassword: true, passwordErrors: errors }
      : null;
  };
}
